import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Modal, Button, Form, Row, Col, Card } from 'react-bootstrap';
import {
  X, Search, CreditCard, CheckCircle, AlertCircle, Printer, ArrowRight,
  Percent, Save, DollarSign, Upload, ImageIcon, Trash2, MapPin, Plus, Minus, FileIcon
} from "lucide-react";
import Swal from "sweetalert2";
import { NumericFormat } from "react-number-format";
import { PDFDocument } from "pdf-lib";
import PropTypes from "prop-types";

import api from "../../../api";
import Globals from "../../../Globals.js";
import formatDate from "../../../utils/formatDate.js";

import FeeSelectorModal from "./TaxCard.js";

import estorn from "../../../assets/img/remover.svg";
import courtesy from "../../../assets/img/courtesy-selected.png";
import "../css/payment-modal.css";

const SHOPPER_ID = 14; // Constante fixa

const PaymentModal = ({
  schedule,
  patient,
  onClose,
  onSavePayment,
  onRefund,
}) => {
  /* ─────────── estados principais ─────────── */
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProcedures, setSelectedProcedures] = useState([]);
  const [payments, setPayments] = useState([{ method: "credit", amount: 0, tax: 0 }]);
  const [paymentStep, setPaymentStep] = useState("select"); // select, address, confirm, success

  /* descontos / total personalizado */
  const [discountType, setDiscountType] = useState("percentage");
  const [discountValue, setDiscountValue] = useState(0);
  const [isEditingTotal, setIsEditingTotal] = useState(false);
  const [customTotal, setCustomTotal] = useState(0);

  /* comprovante geral da transação */
  const [receipts, setReceipts] = useState([]); // [{file, preview}]
  const [receiptPreview, setReceiptPreview] = useState(null);
  const fileInputRef = useRef(null);

  /* estorno */
  const [refundProcedures, setRefundProcedures] = useState([]);

  /* taxas */
  const [showFeeSelector, setShowFeeSelector] = useState(false);
  const [currentIdx, setCurrentIdx] = useState(0);

  /* endereços */
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [newAddress, setNewAddress] = useState({
    street: '', number: '', district: '', city: '',
    state: '', complement: '', zipcode: '', default: false
  });

  const allItems = useMemo(() =>
    (schedule?.schedules ?? []).flatMap((sched) =>
      (sched.items ?? []).map(item => ({
        ...item,
        scheduling_date: sched.scheduling_date // Injeta a data aqui
      }))
    ),
    [schedule]
  );

  const unpaidProcedures = useMemo(() =>
    allItems.filter((item) => item.agreement === null && !item.is_paid),
    [allItems]);


  const paidProcedures = useMemo(() =>
    allItems.filter((item) => item.agreement === null && item.is_paid),
    [allItems]);

  // AGRUPAMENTOS VISUAIS
  const groupedUnpaidProcedures = useMemo(() => {
    return unpaidProcedures.reduce((acc, proc) => {
      const dateKey = proc.scheduling_date || "Sem data";

      if (!acc[dateKey]) acc[dateKey] = [];
      
      acc[dateKey].push(proc);
      
      return acc;
    }, {});
  }, [unpaidProcedures]);

  const groupedPaidProcedures = useMemo(() => {
    return paidProcedures.reduce((acc, proc) => {
      const dateKey = proc.scheduling_date || "Sem data";

      if (!acc[dateKey]) acc[dateKey] = [];
      
      acc[dateKey].push(proc);
      
      return acc;
    }, {});
  }, [paidProcedures]);

  const formatGroupDate = (dateString) => {
    if (!dateString || dateString === "Sem data") return "Sem data";
    
    const [dateScheduling, timeScheduling] = dateString.split(" ");
    const normalizedDate = `${dateScheduling}T00:00:00`;
    const dateFormatted = new Date(normalizedDate).toLocaleDateString("pt-BR");
    
    return timeScheduling ? `${dateFormatted} - ${timeScheduling}` : dateFormatted;
  };

  // Reset ao mudar schedule
  useEffect(() => {
    setSelectedProcedures([]);
    setRefundProcedures([]);
    setPayments([{ method: "credit", amount: 0, tax: 0 }]);
    setPaymentStep("select");
  }, [schedule]);

  // Carregar endereços
  useEffect(() => {
    if (paymentStep !== 'address') return;

    const getAddresses = async () => {
      try {
        const response = await api.get(`api/search/address/customers/${patient.id}`);
        const addrList = response.data.data;
        setAddresses(addrList);

        const defaultAddress = addrList.find(addr => addr.default);
        if (defaultAddress) setSelectedAddress(defaultAddress.id);
      } catch (error) {
        console.error("Erro ao carregar endereços", error);
      }
    };

    getAddresses();
  }, [paymentStep, patient.id]);

  /* ─────────── Cálculos ─────────── */
  const subtotal = useMemo(() =>
    selectedProcedures.reduce((tot, proc) => tot + (Number(proc.selling_price) * (proc.quantity || 1)), 0),
    [selectedProcedures]);

  const calculateDiscount = useCallback(() => {
    if (discountType === "percentage") return (subtotal * discountValue) / 100;
    return discountValue;
  }, [subtotal, discountType, discountValue]);

  const totalAmount = useMemo(() => {
    if (isEditingTotal && customTotal > 0) return customTotal;
    const discount = calculateDiscount();
    return Math.max(0, subtotal - discount);
  }, [isEditingTotal, customTotal, subtotal, calculateDiscount]);

  const paidSoFar = useMemo(() => payments.reduce((sum, p) => sum + p.amount, 0), [payments]);
  const remaining = Math.max(0, totalAmount - paidSoFar);

  const currentNetTotal = useMemo(() => {
    return payments.reduce((acc, payment) => {
      const valor = parseFloat(payment.amount) || 0;
      const taxa = parseFloat(payment.tax) || 0;
      return acc + (valor + (valor * taxa) / 100);
    }, 0);
  }, [payments]);

  const formatCurrency = (val) => {
    const number = Number(val) || 0;
    return `R$ ${number.toFixed(2)}`.replace('.', ',');
  };

  /* ─────────── Handlers ─────────── */
  const handleConfirmFee = ({ brand, installments, tax }) => {
    setPayments((old) =>
      old.map((p, i) => i === currentIdx ? { ...p, brand, installments, tax } : p)
    );
    setShowFeeSelector(false);
  };

  const openSelector = (idx) => {
    setCurrentIdx(idx);
    setShowFeeSelector(true);
  };

  const buildMergedReceipt = async (receiptsList) => {
    const mergedPdf = await PDFDocument.create();
    for (const { file } of receiptsList) {
      const data = await file.arrayBuffer();
      if (file.type === "application/pdf") {
        const donor = await PDFDocument.load(data);
        const pages = await mergedPdf.copyPages(donor, donor.getPageIndices());
        pages.forEach((p) => mergedPdf.addPage(p));
      } else {
        const imgBytes = data;
        let img, dims;
        if (file.type === "image/png") img = await mergedPdf.embedPng(imgBytes);
        else img = await mergedPdf.embedJpg(imgBytes);
        dims = img.scale(1);
        const page = mergedPdf.addPage([dims.width, dims.height]);
        page.drawImage(img, { x: 0, y: 0, width: dims.width, height: dims.height });
      }
    }
    mergedPdf.setTitle("Comprovantes de Pagamento");
    mergedPdf.setAuthor("Achemed");
    return await mergedPdf.save();
  };

  const buildFormData = async () => {
    //const lastSchedule = schedule.schedules[schedule.schedules.length - 1];
    const lastSchedule = schedule.schedules.reduce((max, current) => {
      return current.id > max.id ? current : max;
    }, schedule.schedules[0]);

    const payload = {
      scheduling_id: lastSchedule.id,
      clinic_id: lastSchedule.clinic_id,
      affiliate_company_id: lastSchedule.affiliate_company_id,
      patient: { id: patient.id, name: patient.name, cpf: patient.cpf },
      customer_saved_adresse_id: selectedAddress,
      subtotal,
      discount: {
        type: discountValue > 0 ? discountType : "percentage",
        value: discountValue > 0 ? discountValue : 0,
        amount: discountValue > 0 ? calculateDiscount() : 0,
      },
      total: totalAmount,
      payment_methods: payments.map((p) => ({
        method: p.method,
        amount: p.amount,
        tax: p.tax || 0,
        installments: p.installments || 1,
      })),
      procedures: selectedProcedures.map((p) => ({
        clinic_procedure_id: p.clinic_procedure_id,
        procedure_id: p.procedure_id,
        professional_id: p.professional_id,
        procedure_name: p.procedure_name,
        quantity: p.quantity,
        executing_company: p.executing_company,
        requesting_company: p.requesting_company,
        selling_price: p.selling_price,
        cost_price: p.cost_price,
      })),
    };

    const mergedBytes = await buildMergedReceipt(receipts);
    const blob = new Blob([mergedBytes], { type: "application/pdf" });
    const mergedFile = new File([blob], "comprovantes.pdf", { type: "application/pdf" });

    const form = new FormData();
    form.append("merged_receipt", mergedFile);
    form.append("data", JSON.stringify(payload));

    return form;
  };

  const PAYMENT_OPTIONS = [
    { value: "credit", label: "Cartão de Crédito" },
    { value: "debit", label: "Cartão de Débito" },
    { value: "cash", label: "Dinheiro" },
    { value: "pix", label: "PIX" },
    { value: "courtesy", label: "Cortesia" },
  ];

  const handleQuantityChange = (e, procedureId, delta) => {
    e.stopPropagation();
    setSelectedProcedures((prev) =>
      prev.map((p) => {
        if (p.procedure_id === procedureId) {
          const currentQtd = p.quantity || 1;
          return { ...p, quantity: Math.max(1, currentQtd + delta) };
        }
        return p;
      })
    );
  };

  const isMethodInUse = (method, currentIdx) =>
    payments.some((p, i) => i !== currentIdx && p.method === method);

  const handleMethodChange = (idx, method) => {
    if (isMethodInUse(method, idx)) {
      Swal.fire({ icon: "warning", title: "Método já utilizado", text: "Escolha outro meio de pagamento." });
      return;
    }

    if (method === "courtesy") {
      setPayments([{ method: "courtesy", amount: totalAmount, installments: 1 }]);
    } else {
      setPayments((prev) =>
        prev
          .filter((p) => p.method !== "courtesy")
          .map((p, i) => (i === idx ? { ...p, method, amount: 0, tax: 0 } : p))
      );
    }
  };

  const updatePayment = (idx, key, value) =>
    setPayments((old) => old.map((p, i) => (i === idx ? { ...p, [key]: value } : p)));

  const addPayment = () => {
    const usados = payments.map((p) => p.method);
    const proximo = PAYMENT_OPTIONS.filter((o) => o.value !== "courtesy").find((o) => !usados.includes(o.value));

    if (!proximo) {
      Swal.fire({ icon: "info", title: "Todos os métodos já foram usados" });
      return;
    }
    setPayments((old) => [...old, { method: proximo.value, amount: 0, tax: 0 }]);
  };

  const removePayment = (idx) => setPayments((old) => old.filter((_, i) => i !== idx));

  const allSelected = unpaidProcedures.length > 0 && unpaidProcedures.length === selectedProcedures.length;

  const handleSelectAll = () => {
    if (allSelected) setSelectedProcedures([]);
    else setSelectedProcedures(unpaidProcedures.map(p => ({ ...p, quantity: p.quantity || 1 })));
  };

  const handleSelectProcedure = (procedure) => {
    const isSelected = selectedProcedures.some((p) => p.procedure_id === procedure.procedure_id);
    if (isSelected) {
      setSelectedProcedures((prev) => prev.filter((p) => p.procedure_id !== procedure.procedure_id));
    } else {
      setSelectedProcedures((prev) => [...prev, { ...procedure, quantity: procedure.quantity || 1 }]);
    }
  };

  const handleSelectRefund = (procedure) => {
    const isSelected = refundProcedures.some((p) => p.clinic_procedure_id === procedure.clinic_procedure_id);
    if (isSelected) {
      setRefundProcedures((prev) => prev.filter((p) => p.clinic_procedure_id !== procedure.clinic_procedure_id));
    } else {
      setRefundProcedures((prev) => [...prev, procedure]);
    }
  };

  const openPdfFile = (file) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    window.open(Globals.BASE_URL + '/storage/' + file, '_blank');
  };

  const handleDiscountTypeChange = (type) => {
    setDiscountType(type);
    setDiscountValue(0);
    setIsEditingTotal(false);
    setCustomTotal(0);
  };

  const handleSaveCustomTotal = () => {
    if (customTotal >= 0) {
      const newDiscount = Math.max(0, subtotal - customTotal);
      setDiscountType("fixed");
      setDiscountValue(newDiscount);
      setIsEditingTotal(false);
    }
  };

  const handleFilesChange = (e) => {
    const files = Array.from(e.target.files);
    const newReceipts = files.map((file) => ({
      file,
      preview: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
    }));
    setReceipts((prev) => [...prev, ...newReceipts]);
  };

  const removeReceipt = (idx) => {
    setReceipts((old) => {
      const toRemove = old[idx];
      if (toRemove.preview) URL.revokeObjectURL(toRemove.preview);
      return old.filter((_, i) => i !== idx);
    });
  };

  const handleAdvance = () => {
    if (selectedProcedures.length === 0) return;

    if (remaining > 0.01) { // Tolerância pequena para ponto flutuante
      Swal.fire({
        icon: "warning",
        title: "Ainda faltam",
        text: `Ainda faltam ${formatCurrency(remaining)} para fechar o total.`,
        confirmButtonText: "Voltar",
      });
      return;
    }

    const requiresReceipt = payments.some(p => ["credit", "debit", "pix"].includes(p.method));
    if (requiresReceipt && receipts.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "Comprovante obrigatório",
        text: "Por favor, anexe ao menos um comprovante de pagamento antes de avançar.",
        confirmButtonText: "OK",
      });
      return;
    }

    setPaymentStep("address");
  };

  const handleNewAddressSubmit = async () => {
    try {
      const response = await api.post('api/address/customers', { ...newAddress, customer_id: patient.id });
      const newAddr = response.data.data;
      setAddresses(prev => [...prev, newAddr]);
      if (newAddress.default) setSelectedAddress(newAddr.id);

      setShowAddressModal(false);
      setNewAddress({ street: '', number: '', district: '', city: '', state: '', complement: '', zipcode: '', default: false });
      Swal.fire({ icon: 'success', title: 'Endereço cadastrado!', timer: 1500 });
    } catch (error) {
      console.error(error);
      Swal.fire({ icon: 'error', title: 'Erro ao cadastrar endereço' });
    }
  };

  const handleConfirmPayment = async () => {
    try {
      const formData = await buildFormData();
      if (typeof onSavePayment === "function") await onSavePayment(formData);
      setPaymentStep("success");
    } catch (err) {
      console.error(err);
      Swal.fire({ icon: "error", title: "Erro", text: "Falha ao processar pagamento." });
    }
  };

  const handlePrintReceipt = async (proceduresList) => {
    Swal.fire({ title: 'Gerando Recibo...', didOpen: () => Swal.showLoading() });

    const procsToPrint = proceduresList.filter(proc => proc.cancel !== true)

    try {
      const response = await api.post('api/cashiers/pos/receipt', {
        clinic_procedure_id: procsToPrint[procsToPrint.length - 1].clinic_procedure_id,
        shopper_id: SHOPPER_ID,
        procedures: procsToPrint.map(proc => ({
          procedure_id: proc.procedure_id,
          scheduling_id: proc.scheduling_id
        }))
      });

      const pdfPath = Globals.BASE_URL + '/storage/' + response.data.path;
      Swal.close();

      const result = await Swal.fire({
        icon: 'success', title: 'Recibo gerado!', showConfirmButton: true, confirmButtonText: 'Abrir'
      });

      if (result.isConfirmed) window.open(pdfPath, '_blank');
    } catch (error) {
      Swal.close();
      Swal.fire({ icon: 'error', title: 'Erro ao gerar recibo.' });
    }
  };

  const handleConfirmRefund = () => {
    if (refundProcedures.length === 0) return;

    Swal.fire({
      icon: "warning",
      title: "Confirmar Estorno",
      html: `Estornar <strong>${refundProcedures.length}</strong> procedimento(s)?`,
      showCancelButton: true,
      confirmButtonText: "Sim, estornar",
      confirmButtonColor: "#d33",
    }).then((result) => {
      if (!result.isConfirmed) return;

      if (typeof onRefund === "function") onRefund(refundProcedures);

      api.delete("/api/cashiers/refund-items", {
        data: { clinic_procedure_ids: refundProcedures.map((p) => p.clinic_procedure_id) },
      })
        .then(() => {
          setRefundProcedures([]);
          Swal.fire({ icon: "success", title: "Estorno realizado!" });
        })
        .catch((error) => {
          console.error("Erro estorno:", error);
          Swal.fire({ icon: "error", title: "Erro no estorno" });
        });

      onClose();
    });
  };

  /* Sincronizar Cortesia se total mudar */
  useEffect(() => {
    const hasCourtesy = payments.some((p) => p.method === "courtesy");
    if (hasCourtesy) {
      setPayments((prev) => prev.map((p) => p.method === "courtesy" ? { ...p, amount: totalAmount } : p));
    }
  }, [totalAmount]);

  /* ─────────── Renderização ─────────── */
  return (
    <div className="modal-overlay">
      <div className="procedures-modal payment-modal">
        <div className="modal-header">
          <h2>
            {paymentStep === "select" ? "Pagamento de Procedimentos" :
              paymentStep === "address" ? "Selecionar endereço" :
                paymentStep === "confirm" ? "Confirmar Pagamento" : "Pagamento Concluído"}
          </h2>
          <button className="close-button" onClick={onClose}><X size={24} /></button>
        </div>

        <div className="modal-content-scrollable">
          {paymentStep === "select" && (
            <>
              {/* Info e Busca */}
              <div className="payment-patient-info">
                <div className="payment-patient-name"><span>Paciente:</span> <strong>{patient.name}</strong></div>
                <div className="payment-date"><span>Data:</span> <strong>{formatDate(schedule.schedules[0].date)}</strong></div>
              </div>
              <div className="payment-search-container">
                <div className="search-input">
                  <Search size={20} />
                  <input type="text" placeholder="Pesquisar procedimento" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                </div>
              </div>
              <div className="payment-selection-options">
                <label className="select-all-label">
                  <input type="checkbox" checked={allSelected} onChange={handleSelectAll} />
                  <span>Selecionar todos</span>
                </label>
              </div>

              {/* Lista Procedimentos */}
              <div className="payment-procedures-list">
                <h4>Procedimentos Não Pagos</h4>

                {Object.entries(groupedUnpaidProcedures).map(([date, procs]) => {
                  const filteredProcs = procs.filter(p => !p.cancel && (p.procedure_name?.toLowerCase().includes(searchTerm.toLowerCase())));

                  if (filteredProcs.length === 0) return null;

                  return (
                    <div key={date} className="procedure-date-group mb-3">
                      <h6 className="text-muted mb-2 pb-2">
                        {formatGroupDate(date)}
                      </h6>

                      {filteredProcs.map((proc) => {
                        const selectedItem = selectedProcedures.find(p => p.clinic_procedure_id === proc.clinic_procedure_id);
                        const isSelected = !!selectedItem;
                        const displayQuantity = selectedItem ? (selectedItem.quantity || 1) : (proc.quantity || 1);

                        return (
                          <div key={proc.id} className={`mb-2 payment-procedure-item ${isSelected ? "selected" : ""}`} onClick={() => handleSelectProcedure(proc)}>
                            <input type="checkbox" readOnly checked={isSelected} style={{ margin: 9 }} />
                            <div className="payment-procedure-details"><h4>{proc.procedure_name}</h4></div>

                            <div className="d-flex align-items-center" style={{ minWidth: '100px', justifyContent: 'center' }}>
                              {isSelected ? (
                                <div className="d-flex align-items-center gap-2 bg-white rounded border p-1" onClick={(e) => e.stopPropagation()}>
                                  <button className="btn btn-sm btn-light p-0 d-flex justify-content-center align-items-center" style={{ width: 36, height: 36 }} onClick={(e) => handleQuantityChange(e, proc.procedure_id, -1)}><Minus size={14} /></button>
                                  <span className="fw-bold mx-2" style={{ minWidth: '20px', textAlign: 'center' }}>{displayQuantity}</span>
                                  <button className="btn btn-sm btn-light p-0 d-flex justify-content-center align-items-center" style={{ width: 36, height: 36 }} onClick={(e) => handleQuantityChange(e, proc.procedure_id, 1)}><Plus size={14} /></button>
                                </div>
                              ) : (
                                <span className="text-muted">{displayQuantity}</span>
                              )}
                            </div>

                            {proc.executing_company && <div className="payment-procedure-company payment-procedure-company-not-paid">{proc.executing_company}</div>}
                            <div className="payment-procedure-price">{formatCurrency(proc.selling_price || 0)}</div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}

                {/* Lista Pagos */}
                <h4>Procedimentos Pagos</h4>

                {Object.entries(groupedPaidProcedures).map(([date, procs]) => {
                  const filteredProcs = procs.filter(p => p.procedure_name?.toLowerCase().includes(searchTerm.toLowerCase()));

                  if (filteredProcs.length === 0) return null;

                  return (
                    <div key={date} className="procedure-date-group mb-3">
                      <h6 className="text-muted mb-2 pb-1">
                        {formatGroupDate(date)}
                      </h6>

                      {filteredProcs.map((proc) => {
                        const isSelected = refundProcedures.some((p) => p.procedure_id === proc.procedure_id);
                        return (
                          <div key={proc.id} 
                          className={`mb-2 payment-procedure-item paid ${isSelected ? "selected" : ""}`} 
                          onClick={() => handleSelectRefund(proc)}>
                            <CheckCircle size={24} className={`status-icon status-${proc.confirmed ? "confirmed" : proc.cancel ? "cancelled" : "scheduled"}`} />
                            <div className="payment-procedure-details"><h4>{proc.procedure_name}</h4></div>
                            <span className="fw-bold mx-2" style={{ minWidth: '20px', textAlign: 'center' }}>{proc.quantity}</span>
                            {proc.receipt_files && <div title="Ver comprovante" onClick={openPdfFile(proc.receipt_files)}><FileIcon size={24} /></div>}
                            {proc.executing_company && <div className="payment-procedure-company">{proc.executing_company}</div>}
                            {proc.courtesy && <img src={courtesy} width={50} alt="Cortesia" />}
                            <div className="payment-procedure-price">{formatCurrency(proc.selling_price || 0)}</div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}

              </div>

              {/* Resumo e Pagamento */}
              <div className="payment-summary">
                <div className="payment-subtotal"><span>Subtotal:</span> <span>{formatCurrency(subtotal)}</span></div>
                <div className="payment-discount-section">
                  <h4>Aplicar Desconto</h4>
                  <div className="discount-type-selector">
                    <label className={discountType === "percentage" ? "selected" : ""}><input type="radio" checked={discountType === "percentage"} onChange={() => handleDiscountTypeChange("percentage")} /><Percent size={20} /> Percentual</label>
                    <label className={discountType === "fixed" ? "selected" : ""}><input type="radio" checked={discountType === "fixed"} onChange={() => handleDiscountTypeChange("fixed")} /><DollarSign size={20} /> Valor Fixo</label>
                    <input type="number" min="0" value={discountValue} onChange={(e) => { setDiscountValue(Number(e.target.value)); setIsEditingTotal(false); setCustomTotal(0); }} disabled={isEditingTotal} />
                  </div>
                  {discountValue > 0 && <p>Desconto: {formatCurrency(calculateDiscount())}</p>}
                </div>

                <div className="payment-total">
                  <span>Total a pagar:</span>
                  <div className="payment-total-net"><span className="me-3">Líquido com taxas:</span> <strong>{formatCurrency(currentNetTotal)}</strong></div>
                  {!isEditingTotal ? (
                    <strong>{formatCurrency(totalAmount)}</strong>
                  ) : (
                    <>
                      <input type="number" min="0" step="0.01" value={customTotal} onChange={(e) => setCustomTotal(parseFloat(e.target.value) || 0)} />
                      <button onClick={handleSaveCustomTotal} title="Salvar"><Save size={14} /></button>
                      <button onClick={() => { setIsEditingTotal(false); setCustomTotal(0); }} title="Cancelar"><X size={14} /></button>
                    </>
                  )}
                </div>
              </div>

              {/* Métodos de Pagamento */}
              <div className="payment-methods-container">
                <h3>Formas de Pagamento</h3>
                {payments.map((p, idx) => (
                  <div key={idx} className="payment-row">
                    <select value={p.method} onChange={(e) => handleMethodChange(idx, e.target.value)}>
                      {PAYMENT_OPTIONS.map(({ value, label }) => (
                        <option key={value} value={value} disabled={isMethodInUse(value, idx)}>{label}</option>
                      ))}
                    </select>

                    <NumericFormat
                      className="payment-amount-input"
                      disabled={p.method === "courtesy"}
                      style={{ height: "38px", padding: "0 10px", width: "100%", borderRadius: "6px", border: "1px solid #ccc" }}
                      thousandSeparator="." decimalSeparator="," prefix="R$ " decimalScale={2} fixedDecimalScale
                      value={p.amount === 0 ? '' : p.amount}
                      onValueChange={(values) => updatePayment(idx, "amount", values.floatValue || 0)}
                      placeholder="Valor"
                    />

                    {(p.method === "credit" || p.method === "debit") && (
                      <button type="button" className="select-fee-button" style={{ height: "38px", width: "100%", border: p.brand ? "1px solid green" : "1px solid red" }} onClick={() => openSelector(idx)}>
                        {p.brand ? <span style={{ color: "green" }}>{p.installments}x {p.tax.toFixed(2)}%</span> : <CreditCard size={16} />}
                      </button>
                    )}

                    {payments.length > 1 && <button onClick={() => removePayment(idx)} title="Excluir"><Trash2 size={16} /></button>}
                  </div>
                ))}

                {!payments.some(p => p.method === "courtesy") && remaining > 0.01 && (
                  <button className="add-payment-button" onClick={addPayment}>+ Adicionar ({formatCurrency(remaining)})</button>
                )}

                <div className="payment-receipt-upload">
                  <h4>Comprovantes</h4>
                  <button className="upload-button w-50" onClick={() => fileInputRef.current.click()}><Upload size={18} /> Anexar</button>
                  <input type="file" ref={fileInputRef} multiple accept="image/*,application/pdf" onChange={handleFilesChange} className="file-input-hidden" />
                  {receipts.length > 0 && (
                    <ul className="receipt-list">
                      {receipts.map(({ file, preview }, idx) => (
                        <li key={idx} className="receipt-item">
                          <div className="d-flex flex-column">
                            {preview ? <img src={preview} alt={file.name} /> : <ImageIcon size={24} />}
                            <span className="mt-3">{file.name}</span>
                          </div>
                          <button onClick={() => removeReceipt(idx)}><Trash2 size={24} /></button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Seleção de Endereço */}
          {paymentStep === "address" && (
            <div className="address-selection-container">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="mb-0">Selecionar Endereço</h4>
                <Button variant="outline-primary" size="sm" onClick={() => setShowAddressModal(true)}><Plus size={16} className="me-1" /> Novo Endereço</Button>
              </div>

              <div className="row">
                {
                  addresses.length === 0 ? (
                    <div className="d-flex flex-column justify-content-center align-items-center mt-5">
                      <AlertCircle size={72} className="icon-alert mb-3" />
                      <p className="fs-4">Nenhum endereço encontrado para este paciente.</p>
                      <p className="fs-4">Adicione um novo clicando no botão acima.</p>
                    </div>
                  ) : (
                    addresses.map((address) => (
                      <div key={address.id} className="col-md-6 mb-3">
                        <Card className={`address-card ${selectedAddress === address.id ? 'border-primary' : ''}`} style={{ cursor: 'pointer' }} onClick={() => setSelectedAddress(address.id)}>
                          <Card.Body>
                            <div className="d-flex align-items-start">
                              <Form.Check type="radio" checked={selectedAddress === address.id} onChange={() => setSelectedAddress(address.id)} className="me-3" />
                              <div className="flex-grow-1">
                                <div className="d-flex align-items-center mb-2">
                                  <MapPin size={16} className="text-muted me-1" />
                                  <strong className="text-truncate">{address.street}, {address.number}</strong>
                                  {address.default && <span className="badge bg-primary ms-2">Padrão</span>}
                                </div>
                                <p className="text-muted small mb-1">{address.district}, {address.city}-{address.state}</p>
                                <p className="text-muted small mb-1">CEP: {address.zipcode}</p>
                              </div>
                            </div>
                          </Card.Body>
                        </Card>
                      </div>
                    ))
                  )
                }

                {/* {
                addresses.map((address) => (
                  <div key={address.id} className="col-md-6 mb-3">
                    <Card className={`address-card ${selectedAddress === address.id ? 'border-primary' : ''}`} style={{ cursor: 'pointer' }} onClick={() => setSelectedAddress(address.id)}>
                      <Card.Body>
                        <div className="d-flex align-items-start">
                          <Form.Check type="radio" checked={selectedAddress === address.id} onChange={() => setSelectedAddress(address.id)} className="me-3" />
                          <div className="flex-grow-1">
                            <div className="d-flex align-items-center mb-2">
                              <MapPin size={16} className="text-muted me-1" />
                              <strong className="text-truncate">{address.street}, {address.number}</strong>
                              {address.default && <span className="badge bg-primary ms-2">Padrão</span>}
                            </div>
                            <p className="text-muted small mb-1">{address.district}, {address.city}-{address.state}</p>
                            <p className="text-muted small mb-1">CEP: {address.zipcode}</p>
                          </div>
                        </div>
                      </Card.Body>
                    </Card>
                  </div>
                ))} */}
              </div>

              {/* Modal Novo Endereço Inline */}
              <Modal show={showAddressModal} onHide={() => setShowAddressModal(false)}>
                <Modal.Header closeButton><Modal.Title>Novo Endereço</Modal.Title></Modal.Header>
                <Modal.Body>
                  <Form>
                    <Row>
                      <Col md={8}><Form.Group className="mb-3"><Form.Label>Endereço</Form.Label><Form.Control type="text" value={newAddress.street} onChange={(e) => setNewAddress(p => ({ ...p, street: e.target.value }))} required /></Form.Group></Col>
                      <Col md={4}><Form.Group className="mb-3"><Form.Label>Número</Form.Label><Form.Control type="text" value={newAddress.number} onChange={(e) => setNewAddress(p => ({ ...p, number: e.target.value }))} required /></Form.Group></Col>
                    </Row>
                    <Row>
                      <Col md={6}><Form.Group className="mb-3"><Form.Label>Bairro</Form.Label><Form.Control type="text" value={newAddress.district} onChange={(e) => setNewAddress(p => ({ ...p, district: e.target.value }))} required /></Form.Group></Col>
                      <Col md={6}><Form.Group className="mb-3"><Form.Label>CEP</Form.Label><Form.Control type="text" value={newAddress.zipcode} onChange={(e) => setNewAddress(p => ({ ...p, zipcode: e.target.value }))} required /></Form.Group></Col>
                    </Row>
                    <Row>
                      <Col md={8}><Form.Group className="mb-3"><Form.Label>Cidade</Form.Label><Form.Control type="text" value={newAddress.city} onChange={(e) => setNewAddress(p => ({ ...p, city: e.target.value }))} required /></Form.Group></Col>
                      <Col md={4}><Form.Group className="mb-3"><Form.Label>Estado</Form.Label><Form.Control type="text" value={newAddress.state} onChange={(e) => setNewAddress(p => ({ ...p, state: e.target.value }))} required /></Form.Group></Col>
                    </Row>
                    <Form.Group className="mb-3"><Form.Label>Complemento</Form.Label><Form.Control type="text" value={newAddress.complement} onChange={(e) => setNewAddress(p => ({ ...p, complement: e.target.value }))} /></Form.Group>
                    <Form.Check type="checkbox" label="Padrão" checked={newAddress.default} onChange={(e) => setNewAddress(p => ({ ...p, default: e.target.checked }))} />
                  </Form>
                </Modal.Body>
                <Modal.Footer>
                  <Button variant="secondary" onClick={() => setShowAddressModal(false)}>Cancelar</Button>
                  <Button variant="primary" onClick={handleNewAddressSubmit}>Salvar</Button>
                </Modal.Footer>
              </Modal>
            </div>
          )}

          {/* Confirmação e Sucesso */}
          {paymentStep === "confirm" && (
            <div className="payment-confirmation-container">
              <div className="confirmation-header"><AlertCircle size={24} className="icon-alert" /><p className="confirmation-title">Confirme os detalhes</p></div>
              <div className="confirmation-section">
                <h3 className="section-title">Pagador: {patient.name}</h3>
                <ul className="payment-list">
                  {payments.map((p, i) => <li key={i}><strong>{PAYMENT_OPTIONS.find(o => o.value === p.method)?.label}</strong> – {formatCurrency(p.amount)}</li>)}
                </ul>
              </div>

              <div className="confirmation-section">
                <h3 className="section-title">
                  Procedimentos ({selectedProcedures.length})
                </h3>
                {selectedProcedures.map((proc) => (
                  <p key={proc.code}>
                    {proc.procedure_name} — {formatCurrency(proc.selling_price)}
                  </p>
                ))}
              </div>

              <div className="confirmation-section totals">
                <p>
                  <strong>Subtotal:</strong> {formatCurrency(subtotal)}
                </p>
                {discountValue > 0 && (
                  <p>
                    <strong>Desconto:</strong>{" "}
                    {formatCurrency(calculateDiscount())}
                  </p>
                )}
                <p className="total-line">
                  <strong>Total:</strong> {formatCurrency(totalAmount)}
                </p>
              </div>
            </div>
          )}

          {paymentStep === "success" && (
            <div className="payment-success">
              <div className="payment-success-icon">
                <CheckCircle size={48} />
              </div>
              <div className="payment-success-client">
                <h4>{patient.name}</h4>
                <p>CPF: {patient.cpf}</p>
              </div>

              <h3>Pagamento registrado!</h3>
              <ul className="payment-list">
                {payments.map((p, i) => (
                  <li key={i}>
                    <strong>
                      {p.method === "credit"
                        ? "Crédito"
                        : p.method === "debit"
                          ? "Débito"
                          : p.method === "cash"
                            ? "Dinheiro"
                            : p.method === "courtesy"
                              ? "Cortesia"
                              : "PIX"}
                    </strong>
                    {" – "}
                    {formatCurrency(p.amount)}
                  </li>
                ))}
                <h3>Procedimentos ({selectedProcedures.length})</h3>
                {selectedProcedures.map((proc) => (
                  <p key={proc.code}>
                    {proc.procedure_name} — {formatCurrency(proc.selling_price)}
                  </p>
                ))}

                <li>
                  <strong>Total:</strong> {formatCurrency(totalAmount)}
                </li>
              </ul>
              <p>{new Date().toLocaleString("pt-BR")}</p>
              {receiptPreview && (
                <img
                  src={receiptPreview}
                  alt="Comprovante"
                  className="receipt-preview-success"
                />
              )}
            </div>
          )}

          <FeeSelectorModal show={showFeeSelector} method={payments[currentIdx]?.method} onHide={() => setShowFeeSelector(false)} initialBrand={payments[currentIdx]?.brand} initialInstallments={payments[currentIdx]?.installments} onConfirm={handleConfirmFee} />
        </div>

        {/* Footer Actions */}
        <div className="modal-footer">
          {paymentStep === "select" && (
            <div className="payment-actions">
              <button className="cancel-button" onClick={onClose}>Cancelar</button>
              {selectedProcedures.length > 0 && <button className="process-payment-button" onClick={handleAdvance}><ArrowRight size={18} /> Avançar</button>}
              {paidProcedures.length > 0 && <button className="process-payment-button" onClick={() => handlePrintReceipt(paidProcedures)}><Printer size={18} /> Imprimir Recibo</button>}
              {refundProcedures.length > 0 && <button className="confirm-refund-button" onClick={handleConfirmRefund}><img src={estorn} width={18} alt="Estornar" /> Confirmar Estorno</button>}
            </div>
          )}

          {paymentStep === "address" && (
            <div className="payment-actions">
              <button className="cancel-button" onClick={() => setPaymentStep('select')}>Voltar</button>
              <button className="process-payment-button" onClick={() => selectedAddress ? setPaymentStep('confirm') : Swal.fire({ icon: 'warning', title: 'Selecione um endereço' })}><ArrowRight size={18} /> Avançar</button>
            </div>
          )}

          {paymentStep === "confirm" && (
            <div className="payment-confirmation-actions">
              <button className="back-button" onClick={() => setPaymentStep("address")}>Voltar</button>
              <button className="confirm-payment-button" onClick={handleConfirmPayment}><CheckCircle size={18} /> Confirmar Pagamento</button>
            </div>
          )}

          {paymentStep === "success" && (
            <div className="payment-success-actions">
              <button className="print-receipt-button" onClick={() => handlePrintReceipt(selectedProcedures)}><Printer size={18} /> Imprimir Recibo</button>
              <button className="close-success-button" onClick={onClose}><ArrowRight size={18} /> Concluir</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

PaymentModal.propTypes = {
  schedule: PropTypes.object.isRequired,
  patient: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSavePayment: PropTypes.func.isRequired,
  onRefund: PropTypes.func,
};

export default PaymentModal;